/**
 * The ride's dispatch decision trail on its Admin page (D587; the trail is D586, ADR-0110) — framework-neutral view-model:
 * who may read it, the reading order of the factors, and the words for every reason a driver was left out.
 *
 * The console only presents: the server authorizes the read at the RIDE's market (`dispatch:view`) and every decision, place,
 * score and reason is the server's. Neutral: metres, seconds and dimensionless scores; no country, currency or language here.
 */
import type { DispatchDecision, DispatchDecisionCandidate } from '@vello/api-client';
import { splitReason } from '@vello/driver-eligibility';
import { grantsAllow, type KnownGrants } from './console-access.js';

/** Display only — the server re-authorizes the read at the ride's market. */
export function dispatchDecisionsAuthority(grants: KnownGrants): { readonly mayView: boolean } {
  if (grants === undefined) return { mayView: false };
  return { mayView: grantsAllow(grants, { permission: 'dispatch:view', target: 'GEO' }) };
}

/** The ranking factors, in the order the panel's columns read them (the server's `components` keys). `completion` is the seventh, present only under a governed ranking score (D591). */
export const RANKING_FACTORS = ['eta', 'pickup', 'acceptance', 'completion', 'idle', 'destinationAlignment', 'rating'] as const;
export type RankingFactor = (typeof RANKING_FACTORS)[number];

/** The factors one decision shows: the six legacy ones always, the completion rate only when the decision weighed it (a governed ranking score in ACTIVE mode). */
export function factorsOf(d: DispatchDecision): readonly RankingFactor[] {
  const weighsCompletion = d.ranking.weightsBasisPoints.completion !== undefined || d.candidates.some((c) => c.components?.completion !== undefined);
  return RANKING_FACTORS.filter((f) => f !== 'completion' || weighsCompletion);
}

/** Whether the decision recorded where a SHADOW ranking score would have put its drivers (D591): the column is shown only then. */
export function hasShadowRank(d: DispatchDecision): boolean {
  return d.candidates.some((c) => c.shadow !== undefined);
}

/** A ranked driver has a place and a score; an excluded one has its reasons instead (never both, D586). */
export function isRankedCandidate(c: DispatchDecisionCandidate): boolean {
  return c.place !== undefined && c.score !== undefined && c.excludedReasons.length === 0;
}

export interface TrailWords {
  readonly v: (id: string, code: string | null | undefined, vars?: Readonly<Record<string, string | number>>) => string;
  readonly has: (id: string, code: string | null | undefined) => boolean;
  /** The name of a document type of the market's KYC catalog (a document reason's argument). */
  readonly documentType: (code: string) => string;
}

/**
 * The words for one reason a driver was left out. The trail carries the eligibility pipeline's reasons, the auto-pause, the
 * wallet refusals and the service decision's unreadable state (`dispatch.candidate_exclusion_reason`), the trail's own reason
 * (`dispatch.wave_trail_reason`), and the ONE service decision's `CODE` / `CODE:ARG` reasons — documents, vehicle, onboarding,
 * account (`driver.readiness_reason`, then `driver.eligibility_reason`). An unknown code reads as the translated
 * "uncatalogued value", never the code.
 */
export function trailReasonText(reason: string, words: TrailWords): string {
  const { code, arg } = splitReason(reason);
  if (words.has('dispatch.candidate_exclusion_reason', code)) return words.v('dispatch.candidate_exclusion_reason', code);
  if (words.has('dispatch.wave_trail_reason', code)) return words.v('dispatch.wave_trail_reason', code);
  const vars = { item: words.v('driver.onboarding_item', arg ?? ''), type: arg !== undefined ? words.documentType(arg) : '' };
  if (words.has('driver.readiness_reason', code)) return words.v('driver.readiness_reason', code, vars);
  if (words.has('driver.eligibility_reason', code)) return words.v('driver.eligibility_reason', code, vars);
  return words.v('dispatch.candidate_exclusion_reason', code);
}

/** A system actor (`system:…`) is named by the catalog; any other actor is the operator who sent the wave. */
export function isSystemActor(actor: string): boolean {
  return actor.startsWith('system:');
}
